import React, { useState } from 'react'
import { Check } from 'lucide-react'
import './Checkbox.css'

/**
 * Reusable Checkbox Component
 *
 * A customizable checkbox with label and error handling
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Checkbox label
 * @param {string} props.description - Optional description text below label
 * @param {boolean} props.checked - Checked state
 * @param {Function} props.onChange - Change handler
 * @param {string} props.error - Error message
 * @param {boolean} props.required - Required field
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.size - Checkbox size: 'small', 'medium', 'large' (default: 'medium')
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref for React Hook Form compatibility
 */
const Checkbox = React.forwardRef(
	(
		{
			label,
			description,
			checked,
			onChange,
			error,
			required = false,
			disabled = false,
			size = 'medium',
			className = '',
			id,
			name,
			value,
			...props
		},
		ref
	) => {
		const [isFocused, setIsFocused] = useState(false)

		const checkboxId = id || `checkbox-${name || label?.replace(/\s+/g, '-').toLowerCase()}`
		const baseClass = 'checkbox'
		const wrapperClasses = [
			baseClass,
			error && `${baseClass}--error`,
			disabled && `${baseClass}--disabled`,
			isFocused && `${baseClass}--focused`,
			`${baseClass}--${size}`,
			className,
		]
			.filter(Boolean)
			.join(' ')

		return (
			<div className={wrapperClasses}>
				<label htmlFor={checkboxId} className={`${baseClass}__container`}>
					<input
						id={checkboxId}
						type="checkbox"
						name={name}
						value={value}
						checked={checked}
						onChange={onChange}
						disabled={disabled}
						required={required}
						className={`${baseClass}__input`}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						ref={ref}
						{...props}
					/>

					<span className={`${baseClass}__box`}>
						{checked && (
							<Check
								className={`${baseClass}__icon`}
								size={size === 'small' ? 14 : size === 'large' ? 20 : 16}
							/>
						)}
					</span>

					<div className={`${baseClass}__content`}>
						<span className={`${baseClass}__label`}>
							{label}
							{required && <span className={`${baseClass}__required`}>*</span>}
						</span>
						{description && <span className={`${baseClass}__description`}>{description}</span>}
					</div>
				</label>

				{error && <span className={`${baseClass}__error`}>{error}</span>}
			</div>
		)
	}
)

Checkbox.displayName = 'Checkbox'

export default Checkbox
